import { AlertModal } from '@/components/AlertModal';
import { GenericSelect } from '@/components/GenericSelect';
import { MediaPreview } from '@/components/MediaPreview';
import { useHasScopes } from '@/hooks/useHasScopes';
import BaseLayout from '@/layout/BaseLayout';
import { RestrictedComponent } from '@/layout/RestrictedComponent';
import { trpc } from '@/trpcClient';
import { logger } from '@/utils/logger';
import { campaignDisplayFormatMapper } from '@/utils/mapper';
import { useDeviceDetect } from '@/utils/useMediaQuery';
import { usePreventLeave } from '@/utils/usePreventLeave';
import { type EditMediaInputType, checkMediaEditInput } from '@shelflix/schemas/media/edit';
import { EDisplayFormat } from '@shelflix/schemas/_common/enum';
import type { BrandDto } from '@shelflix/trpc/routes/brand/list/useCase';
import Dropzone, { CropArea } from '@shelflix/ui-web/components/Dropzone/index';
import Icon from '@shelflix/ui-web/components/Icon';
import { Button } from '@shelflix/ui-web/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@shelflix/ui-web/components/ui/card';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  scrollToError,
} from '@shelflix/ui-web/components/ui/form';
import { Input } from '@shelflix/ui-web/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@shelflix/ui-web/components/ui/select';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import { useMediaFormStore } from './store';

/**
 * Converts a File object to a base64 string without the data URL prefix.
 */
async function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result as string;
      // Remove data URL prefix (e.g., "data:image/jpeg;base64,")
      const base64 = result.split(',')[1];
      resolve(base64);
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

export function MediaEdit() {
  const { mediaUuid } = useParams<{ mediaUuid: string }>();
  const { isMobile } = useDeviceDetect();

  // Permission checks
  const canDeleteMedia = useHasScopes(['media:delete']);

  const [media, setMedia] = useState<File | null>(null);
  const [newMedia, setNewMedia] = useState(false);
  const dropzoneRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const thumbnailDropzoneRef = useRef<HTMLDivElement>(null);
  const [thumbnail, setThumbnail] = useState<File | string | null>(null);
  const [crop, setCrop] = useState<CropArea | undefined>(undefined);
  const [thumbnailRemoved, setThumbnailRemoved] = useState(false);

  const [openAlertModal, setOpenAlertModal] = useState(false);

  const [isLoading, setLoading] = useState(false);
  const [isUploading, setIsUploading] = useState(false);

  const [campaigns, setCampaigns] = useState<
    {
      uuid: string;
      name: string;
    }[]
  >([]);

  const { data: mediaToEdit, isFetching } = useQuery(
    trpc.media.get.queryOptions(
      {
        uuid: mediaUuid ?? '',
      },
      {
        enabled: !!mediaUuid,
        refetchOnWindowFocus: false,
      }
    )
  );

  const { formData, resetForm } = useMediaFormStore();

  useEffect(() => {
    if (mediaToEdit) {
      setLoading(true);

      form.reset({
        ...mediaToEdit,
        fileType: '',
        brand: mediaToEdit.brand,
        displayFormat: mediaToEdit.displayFormat as EDisplayFormat,
        extension: mediaToEdit.extension ?? undefined,
      });
      if (mediaToEdit.thumbnailUrl) {
        setThumbnail(mediaToEdit.thumbnailUrl);
      }
      setCampaigns(
        mediaToEdit.campaigns.map((campaign) => ({
          uuid: campaign.uuid,
          name: campaign.name,
        }))
      );
      urlToFile(mediaToEdit.mediaUrl, mediaToEdit.filename).then((file) => {
        if (file) {
          setMedia(file);
          form.setValue('fileType', file?.type);
        }
        setLoading(false);
      });
    }
  }, [mediaToEdit]);

  // useEffect(() => form.reset(), [media]);

  async function urlToFile(url: string, filename: string, mimeType?: string): Promise<File | null> {
    const response = await fetch(url);
    const blob = await response.blob();
    const type = mimeType || blob.type;
    return new File([blob], filename, { type });
  }

  const form = useForm<
    EditMediaInputType & {
      brand: {
        uuid: string;
        name: string;
      };
    }
  >({
    defaultValues: formData,
    mode: 'all',
  });

  const query = useQuery({
    ...trpc.brand.listForMedia.queryOptions({
      recordsPerPage: 9999,
    }),
  });
  const editMediaMutation = useMutation(trpc.media.edit.mutationOptions());
  const uploadImageMutation = useMutation(trpc.image.uploadCompressed.mutationOptions());

  const onSubmit = async (
    data: EditMediaInputType & {
      brand: {
        uuid: string;
        name: string;
      };
    }
  ) => {
    if (editMediaMutation.isPending || uploadImageMutation.isPending) return;
    if (thumbnailRemoved && !thumbnail) {
      toast.error('Selecione uma nova thumbnail antes de salvar.');
      return;
    }

    if (mediaUuid) {
      try {
        setIsUploading(true);

        const name = media ? media?.name : 'arquivo';
        const extension = media ? (media.type.split('/').pop() ?? '') : '';
        const size = media ? media.size : 0;
        const isVideo = ['mp4', 'mov', 'webm'].includes(extension.toLowerCase());

        let duration = 0;
        if (isVideo && media) {
          const url = URL.createObjectURL(media);
          duration = await new Promise<number>((resolve) => {
            const video = document.createElement('video');
            video.preload = 'metadata';
            video.src = url;
            video.onloadedmetadata = () => {
              resolve(video.duration);
              URL.revokeObjectURL(url);
            };
          });
        }

        // First, upload the thumbnail with compression if there's a new one
        let thumbnailKey: string | undefined;
        if (thumbnail && thumbnail instanceof File) {
          const base64Data = await fileToBase64(thumbnail);
          const uploadResult = await uploadImageMutation.mutateAsync({
            base64Data,
            filename: thumbnail.name,
            imageType: 'media-thumbnail',
            cropOptions: crop,
          });
          thumbnailKey = uploadResult.key;
          logger.info('Thumbnail uploaded', {
            originalSize: uploadResult.originalSize,
            compressedSize: uploadResult.compressedSize,
            savingsPercent: uploadResult.savingsPercent,
          });
        }

        const payload: EditMediaInputType = {
          uuid: mediaUuid,
          name: data.name,
          filename: name,
          brand: typeof data.brand === 'string' ? data.brand : (data.brand as { uuid: string }).uuid,
          displayFormat: data.displayFormat,
          active: data.active,
          duration: Math.floor(duration),
          size,
          extension,
          fileType: media?.type ?? '',
          ...(thumbnailKey && { thumbnailKey }), // Use pre-uploaded key if available
          ...(thumbnailRemoved && !thumbnail && { thumbnailFileType: 'REMOVE' }),
        };

        const response = await editMediaMutation.mutateAsync(payload);

        // Upload the main media file (video/image) - this still uses presigned URL
        if (media) {
          const formData = new FormData();
          Object.entries(response.uploadUrl.fields ?? {}).forEach(([key, value]) => {
            formData.append(key, value);
          });
          formData.append('file', media);
          await fetch(response.uploadUrl.url, {
            method: 'POST',
            body: formData,
          });
        }

        resetForm();
        navigate(-1);
      } catch (error) {
        toast.error(`Erro ao editar mídia: ${(error as Error).message}`);
      } finally {
        setIsUploading(false);
      }
    }
  };

  usePreventLeave(isUploading);

  const deleteMediaMutation = useMutation(trpc.media.delete.mutationOptions());

  const confirmDelete = async () => {
    if (!mediaUuid) return;
    deleteMediaMutation.mutate(
      {
        uuid: mediaUuid,
      },
      {
        onSuccess: async (_response) => {
          toast.success('Mídia deletada com sucesso');
          navigate('/media');
        },
        onError: (error) => {
          toast.error(`Erro ao deletar mídia: ${error.message}`);
        },
        onSettled: () => {
          setOpenAlertModal(false);
        },
      }
    );
  };

  const migrateMediaMutation = useMutation(trpc.migration.mediaMigration.mutationOptions());

  return (
    <BaseLayout>
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit, scrollToError)} className="flex w-full flex-col gap-8">
          <Card className="rounded-[10px]">
            <CardHeader className="pb-0">
              <CardTitle className="text-primary text-xl leading-5 font-semibold md:text-2xl">
                {!media ? 'Carregar mídia' : 'Simulação e informações'}
              </CardTitle>
            </CardHeader>

            <CardContent>
              <FormField
                control={form.control}
                name="fileType"
                rules={{ validate: (value) => checkMediaEditInput('fileType', value) }}
                render={({ field }) => (
                  <FormItem className="flex flex-col gap-0.75">
                    <FormControl>
                      {(!mediaToEdit?.mediaUrl && isLoading) || newMedia ? (
                        <Dropzone
                          removable={true}
                          onFilesSelected={(files) => {
                            setMedia(files[0]);
                            field.onChange(files[0]?.type);
                          }}
                          ref={dropzoneRef}
                          title="Arraste ou clique aqui para selecionar seu arquivo"
                          description="Extensões permitidas: MP4, GIF, JPG, PNG Proporção recomendada: 16:9 (Máx. 100mb)"
                          value={media ? [media] : undefined}
                          mimeTypes={['video/mp4', 'image/gif', 'image/jpeg', 'image/png']}
                          maxSizeInBytes={100 * 1024 * 1024}
                        />
                      ) : (
                        <MediaPreview
                          file={media}
                          onReplace={() => [setMedia(null), setNewMedia(true)]}
                          onDelete={() => setOpenAlertModal(true)}
                          showDeleteButton={canDeleteMedia}
                          loading={isLoading || isFetching}
                        />
                      )}
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </CardContent>
          </Card>

          {canDeleteMedia && (
            <AlertModal
              openModal={openAlertModal}
              onOpenChange={setOpenAlertModal}
              icon={<Icon name="calendar_add_on" className="text-primary" size={40} fill={1} />}
              title="Atenção! Confirme esta ação"
              subtitle="Click no botão de excluir para deletar essa mídia"
              content={
                <p className="font-[Figtree] text-base text-[#DC3545]">
                  Esta não é uma ação reversível. Ao clicar em excluir, a mídia será deletada da nossa base de dados.
                  Caso essa mídia esteja associada a uma campanha, a operação irá falhar.
                </p>
              }
              actionBotton={
                <div className="flex flex-row gap-2">
                  <Button variant="outline" onClick={confirmDelete} className="w-1/2">
                    <Icon name="delete" className="text-primary" fill={1} />
                    <span className="text-primary">Excluir</span>
                  </Button>

                  <Button onClick={() => setOpenAlertModal(false)} className="w-1/2">
                    <Icon name="close" className="text-white" fill={1} />
                    <span className="text-white">Cancelar</span>
                  </Button>
                </div>
              }
            />
          )}
          <div className="flex flex-col gap-4 sm:flex-row">
            <Card className="rounded-[10px]">
              <CardHeader className="pb-0">
                <CardTitle className="text-primary text-xl leading-5 font-semibold md:text-2xl">Thumbnail</CardTitle>
              </CardHeader>
              <CardContent>
                {thumbnail && typeof thumbnail === 'string' ? (
                  <div className="flex flex-col gap-4">
                    <div style={{ maxWidth: 180 }} className="mx-auto flex aspect-9/16 items-center justify-center">
                      <img
                        src={thumbnail}
                        alt="Thumbnail atual"
                        className="h-full w-full rounded object-cover"
                        onError={() => {
                          setThumbnail('https://shelflix.com.br/wp-content/uploads/2025/02/Grey_Orange-300x90.png');
                        }}
                      />
                    </div>
                    <div className="flex justify-center gap-2">
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => {
                          setThumbnail(null);
                          setThumbnailRemoved(true);
                        }}
                        className="w-fit"
                      >
                        <Icon name="compare_arrows" />
                        Alterar thumbnail
                      </Button>
                    </div>
                  </div>
                ) : (
                  <div style={{ maxWidth: 180 }} className="mx-auto aspect-9/16">
                    <Dropzone
                      removable={true}
                      onFilesSelected={(files) => {
                        setThumbnail(files[0]);
                        setThumbnailRemoved(false);
                      }}
                      ref={thumbnailDropzoneRef}
                      title="Arraste ou clique aqui para selecionar a thumbnail"
                      description="Extensões permitidas: JPG, PNG. Proporção obrigatória: 9:16 (Máx. 5mb)"
                      value={typeof thumbnail === 'object' && thumbnail !== null ? [thumbnail as File] : undefined}
                      requiredAspectRatio="9:16"
                      className="h-full w-full"
                      readonly={false}
                      cropModal={true}
                      cropValue={crop}
                      onCropChange={(crop) => setCrop(crop)}
                      variant="thumb"
                    />
                  </div>
                )}
              </CardContent>
            </Card>

            <Card className="flex-1 rounded-[10px]">
              <CardHeader className="pb-0">
                <CardTitle className="text-primary text-xl leading-5 font-semibold md:text-2xl">
                  Informações de mídia
                </CardTitle>
              </CardHeader>
              <CardContent className="flex flex-1 flex-col gap-[27px_12px] pt-4">
                <FormField
                  control={form.control}
                  name="name"
                  rules={{ validate: (value) => checkMediaEditInput('name', value) }}
                  render={({ field }) => (
                    <FormItem className="flex flex-col gap-0.75">
                      <FormLabel className="text-xs font-medium text-(--gray-primary)">Nome</FormLabel>
                      <FormControl>
                        <Input className="h-10" placeholder="Digite o nome da mídia" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <div className="flex w-full flex-col flex-wrap gap-4 sm:flex-row">
                  <FormField
                    control={form.control}
                    name="brand"
                    rules={{ validate: (value) => checkMediaEditInput('brand', value.uuid ?? value) }}
                    render={({ field }) => (
                      <FormItem className="flex grow flex-col gap-0.75 md:basis-60">
                        <FormLabel className="text-xs font-medium text-(--gray-primary)">Marca</FormLabel>
                        <FormControl>
                          <GenericSelect<BrandDto>
                            onSelect={(brand) => field.onChange(brand.uuid ?? brand)}
                            value={field.value}
                            loading={query.isLoading}
                            dependencies={[query.data]}
                            fetchItems={async (search) => {
                              const brands = (query.data?.brands ?? []).sort((a, b) => a.name.localeCompare(b.name));
                              if (!search) return brands;
                              return brands.filter((brand) => brand.name.toLowerCase().includes(search.toLowerCase()));
                            }}
                            renderItem={(brand) => (
                              <div>
                                <div className="text-sm font-medium">{brand.name}</div>
                                <div className="text-muted-foreground text-xs">{brand.category}</div>
                              </div>
                            )}
                            getItemLabel={(brand) => brand.name}
                            getItemKey={(brand) => brand.uuid}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="displayFormat"
                    rules={{ validate: (value) => checkMediaEditInput('displayFormat', value) }}
                    render={({ field }) => (
                      <FormItem className="flex grow flex-col gap-0.75 md:basis-60">
                        <FormLabel className="text-xs font-medium text-(--gray-primary)">Formato de exibição</FormLabel>
                        <FormControl>
                          <Select
                            defaultValue={field.value ?? undefined}
                            value={field.value ?? undefined}
                            onValueChange={(displayFormat: EDisplayFormat) => {
                              field.onChange(displayFormat);
                            }}
                          >
                            <SelectTrigger className="h-10 w-full">
                              <SelectValue placeholder="Selecione uma opção" />
                            </SelectTrigger>
                            <SelectContent className=" ">
                              {Object.values(EDisplayFormat).map((displayFormat) => (
                                <SelectItem key={displayFormat} value={displayFormat}>
                                  {campaignDisplayFormatMapper(displayFormat)}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="active"
                    render={({ field }) => (
                      <FormItem className="flex grow flex-col gap-0.75 md:basis-60">
                        <FormLabel className="text-xs font-medium text-(--gray-primary)">Status</FormLabel>
                        <FormControl>
                          <Select value={String(field.value)} onValueChange={(val) => field.onChange(val === 'true')}>
                            <SelectTrigger className="h-10 w-full">
                              <SelectValue placeholder="Selecione uma opção" />
                            </SelectTrigger>
                            <SelectContent className=" ">
                              <SelectItem value={'true'}>Ativa</SelectItem>
                              <SelectItem value={'false'}>Inativa</SelectItem>
                            </SelectContent>
                          </Select>
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
                {mediaToEdit?.externalId === null && (
                  <div className="mt-auto flex w-full flex-col">
                    <Button
                      className="ml-auto w-fit"
                      onClick={(e) => {
                        e.preventDefault();
                        migrateMediaMutation.mutate(
                          {
                            mediaUuid: mediaUuid ?? '',
                          },
                          {
                            onSuccess: () => {
                              toast.success('Migração realizada com sucesso');
                            },
                            onError: (error) => {
                              toast.error(`Erro ao realizar migração: ${error.message}`);
                            },
                          }
                        );
                      }}
                      disabled={migrateMediaMutation.isPending}
                    >
                      Fazer migração
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {!isMobile && (
            <Card className="rounded-[10px]">
              <CardHeader className="pb-0">
                <CardTitle className="text-primary text-xl leading-5 font-semibold md:text-2xl">
                  Campanhas vinculadas
                </CardTitle>
              </CardHeader>
              <CardContent className="grid grid-cols-2 gap-2">
                {campaigns.map((campaign) => (
                  <div
                    key={campaign.uuid}
                    className="bg-secondary flex w-106.25 items-center justify-between gap-2 rounded-[5px] p-2"
                  >
                    <div className="flex items-center gap-2">
                      <span className="text-primary text-sm font-medium">{campaign.name}</span>
                    </div>
                  </div>
                ))}
              </CardContent>
            </Card>
          )}

          <div className="mb-10 flex grow-0 justify-center">
            <RestrictedComponent
              requiredScopes={['media:edit']}
              behavior="disable"
              showToast
              toastMessage="Você não tem permissão para editar mídias."
            >
              <Button
                type="submit"
                loading={editMediaMutation.isPending || isUploading}
                className="bg-primary hover:bg-primary flex grow cursor-pointer items-center gap-2.5 rounded-[5px] px-12 py-2.5 font-semibold text-white sm:w-fit sm:grow-0"
              >
                <Icon name="save" color="#fff" />
                <span className="font-['Figtree'] text-sm">Salvar</span>
              </Button>
            </RestrictedComponent>
          </div>
        </form>
      </Form>
    </BaseLayout>
  );
}
